import React, { useState } from 'react';
import {
  Box, Button, Checkbox, Collapse, FormControlLabel, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import type { ChildCost, CreditLine, Debt, DebtOverride, HouseholdModel, Inputs } from '../../services/household-retirement-service';
import { NumField, produce } from './fields';
import { money } from './format';

type CostKind = 'childcare' | 'activities' | 'post_secondary';

const DEFAULTS: Record<CostKind, ChildCost> = {
  childcare: { person_id: '', monthly: 478.5, from_age: 1, to_age: 6 },
  activities: { person_id: '', monthly: 150, from_age: 3, to_age: 18 },
  post_secondary: { person_id: '', annual: 20000, from_age: 18, to_age: 22 },
};
const LABEL: Record<CostKind, string> = { childcare: 'Childcare', activities: 'Activities', post_secondary: 'Post-secondary' };

/**
 * Spending: the 12-month baseline (derived, or overridden), retirement
 * spending %, per-child life-stage costs, RESP contributions, and the debts
 * read from the liability accounts with editable rate and payment.
 */
export const ExpensesEditor: React.FC<{
  model: HouseholdModel;
  inputs?: Inputs | null;
  onChange: (m: HouseholdModel) => void;
}> = ({ model, inputs, onChange }) => {
  const [showBaseline, setShowBaseline] = useState(false);
  const set = (fn: (d: HouseholdModel) => void) => onChange(produce(model, fn));
  const kids = model.people.filter((p) => p.role === 'child');
  const derived = inputs?.baseline.annual ?? 0;
  const override = model.expenses.baseline_annual_override;
  const currency = inputs?.currency ?? 'CAD';

  const costOf = (kind: CostKind, pid: string) => model.expenses[kind].find((c) => c.person_id === pid);
  const setCost = (kind: CostKind, pid: string, fn: ((c: ChildCost) => void) | null) => set((d) => {
    const list = d.expenses[kind];
    const idx = list.findIndex((c) => c.person_id === pid);
    if (fn === null) {
      if (idx >= 0) list.splice(idx, 1);
      return;
    }
    if (idx < 0) list.push({ ...DEFAULTS[kind], person_id: pid });
    fn(list[idx < 0 ? list.length - 1 : idx]);
  });

  const debts: Debt[] = inputs?.debts ?? [];
  const ov = (id?: string): DebtOverride | undefined => (model.debts ?? []).find((o) => o.account_id === id);
  const setDebt = (id: string | undefined, fn: (o: DebtOverride) => void) => set((d) => {
    d.debts = d.debts ?? [];
    let o = d.debts.find((x) => x.account_id === id);
    if (!o) {
      o = { account_id: id };
      d.debts.push(o);
    }
    fn(o);
  });

  return (
    <Stack spacing={2} data-testid="expenses-editor">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="body2">
            Derived baseline: <b className="fw-num">{money(derived, currency)}/yr</b>
            {inputs && <> ({inputs.baseline.window_start} – {inputs.baseline.window_end})</>}
          </Typography>
          <Button size="small" onClick={() => setShowBaseline((v) => !v)}>{showBaseline ? 'Hide' : 'Show'} how it was derived</Button>
        </Box>
        <NumField label="Override baseline $/yr" value={override ?? null} sx={{ width: 220 }}
          helperText="blank = use the derived baseline"
          onChange={(v) => set((d) => { d.expenses.baseline_annual_override = v; })} />
        <NumField label="Retirement spending %" value={model.expenses.retirement_spending_pct} sx={{ width: 220 }}
          helperText="of the baseline, once all adults retire"
          onChange={(v) => set((d) => { d.expenses.retirement_spending_pct = v ?? 70; })} />
        <NumField label="RESP $/child/yr" value={model.expenses.resp_annual_per_child} sx={{ width: 200 }}
          helperText="CESG adds 20% (max $500)"
          onChange={(v) => set((d) => { d.expenses.resp_annual_per_child = v ?? 0; })} />
      </Stack>
      <Collapse in={showBaseline}>
        {inputs && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} data-testid="baseline-detail">
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Included</Typography>
              {Object.entries(inputs.baseline.included).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <Typography key={k} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{k}</span><span className="fw-num">{money(v, currency)}</span>
                </Typography>
              ))}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Left out</Typography>
              {Object.entries(inputs.baseline.excluded).map(([k, v]) => (
                <Typography key={k} variant="body2" title={v.reason} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{k} <Typography component="span" variant="caption" color="text.secondary">— {v.reason}</Typography></span>
                  <span className="fw-num">{money(v.amount, currency)}</span>
                </Typography>
              ))}
            </Box>
          </Stack>
        )}
      </Collapse>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Children's life-stage costs (today's dollars)</Typography>
        {kids.length === 0 && <Typography variant="body2" color="text.secondary">No children in the plan.</Typography>}
        {kids.map((k) => (
          <Stack key={k.id} direction={{ xs: 'column', lg: 'row' }} spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }} data-testid={`kid-costs-${k.id}`}>
            <Typography variant="body2" sx={{ width: 80, pt: 1 }}><b>{k.name}</b></Typography>
            {(['childcare', 'activities', 'post_secondary'] as CostKind[]).map((kind) => {
              const c = costOf(kind, k.id);
              const amountKey = kind === 'post_secondary' ? 'annual' : 'monthly';
              return (
                <Stack key={kind} direction="row" spacing={1} alignItems="center">
                  <FormControlLabel sx={{ mr: 0 }} label={LABEL[kind]}
                    control={<Checkbox size="small" checked={!!c} inputProps={{ 'aria-label': `${LABEL[kind]} for ${k.name}` }}
                      onChange={(e) => setCost(kind, k.id, e.target.checked ? () => undefined : null)} />} />
                  {c && (
                    <>
                      <NumField label={amountKey === 'annual' ? '$/yr' : '$/mo'} value={c[amountKey] ?? 0} sx={{ width: 120 }}
                        inputProps={{ 'aria-label': `${LABEL[kind]} amount for ${k.name}` }}
                        onChange={(v) => setCost(kind, k.id, (x) => { x[amountKey] = v ?? 0; })} />
                      {kind === 'childcare' && (
                        <TextField size="small" type="month" label="Started" value={c.from_date ?? ''} sx={{ width: 150 }}
                          InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': `Childcare start for ${k.name}` }}
                          onChange={(e) => setCost(kind, k.id, (x) => { x.from_date = e.target.value || undefined; })} />
                      )}
                      <NumField label="From age" value={c.from_age} sx={{ width: 96 }}
                        onChange={(v) => setCost(kind, k.id, (x) => { x.from_age = v ?? 0; })} />
                      <NumField label="To age" value={c.to_age} sx={{ width: 96 }}
                        onChange={(v) => setCost(kind, k.id, (x) => { x.to_age = v ?? 0; })} />
                    </>
                  )}
                </Stack>
              );
            })}
          </Stack>
        ))}
        <Typography variant="caption" color="text.secondary">
          Post-secondary is paid from the RESP first; the rest comes out of cash flow.
        </Typography>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Debts (until payoff)</Typography>
        <Stack spacing={1.5} aria-label="Debts" role="list">
          {debts.map((dbt) => {
            const o = ov(dbt.account_id);
            const rateType = o?.rate_type ?? (dbt.rate_type as 'fixed' | 'variable' | undefined) ?? 'fixed';
            const spreadVal = o?.spread_pct ?? dbt.spread_pct ?? null;
            // A rate the household has not confirmed (none on file, or a 0% placeholder) shows empty with a hint.
            const unconfirmed = (dbt.rate_assumed || dbt.rate_implied) && o?.rate_pct == null && !(rateType === 'variable' && spreadVal != null);
            const rateHint = dbt.rate_implied
              ? `${dbt.rate_pct}% implied from payment — enter rate`
              : `assumed ${model.assumptions.default_loan_rate_pct}% — enter your rate`;
            return (
              <Box key={dbt.account_id ?? dbt.name} role="listitem" data-testid={`debt-${dbt.account_id ?? dbt.name}`}
                sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, opacity: o?.exclude ? 0.55 : 1 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }} flexWrap="wrap" useFlexGap>
                  <Box sx={{ flex: '1 1 320px', minWidth: 260 }}>
                    <TextField size="small" fullWidth multiline maxRows={3} label="Plan label" value={o?.label ?? dbt.label ?? ''}
                      placeholder={dbt.name} inputProps={{ 'aria-label': `Label for ${dbt.name}` }}
                      onChange={(e) => setDebt(dbt.account_id, (x) => { x.label = e.target.value || undefined; })} />
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                      {dbt.name} · {money(dbt.balance, currency)}{dbt.term_end ? ` · term ${dbt.term_start ? `${dbt.term_start} – ` : 'to '}${dbt.term_end}` : ''}
                    </Typography>
                    {dbt.payment_history && <Typography variant="caption" color="text.secondary" component="div">{dbt.payment_history}</Typography>}
                    {dbt.term_note && <Typography variant="caption" color="warning.main" component="div">{dbt.term_note}</Typography>}
                  </Box>
                  <TextField select size="small" label="Kind" value={o?.kind ?? dbt.kind} sx={{ minWidth: 150 }}
                    inputProps={{ 'aria-label': `Kind of ${dbt.name}` }}
                    onChange={(e) => setDebt(dbt.account_id, (x) => { x.kind = e.target.value as DebtOverride['kind']; })}>
                    <MenuItem value="mortgage">Mortgage</MenuItem>
                    <MenuItem value="heloc">HELOC</MenuItem>
                    <MenuItem value="auto">Auto loan</MenuItem>
                    <MenuItem value="loan">Loan</MenuItem>
                  </TextField>
                  <TextField select size="small" label="Rate type" value={rateType} sx={{ minWidth: 130 }}
                    inputProps={{ 'aria-label': `Rate type for ${dbt.name}` }}
                    onChange={(e) => setDebt(dbt.account_id, (x) => { x.rate_type = e.target.value as 'fixed' | 'variable'; })}>
                    <MenuItem value="fixed">Fixed</MenuItem>
                    <MenuItem value="variable">Variable</MenuItem>
                  </TextField>
                  {rateType === 'variable' ? (
                    <NumField label="Spread to prime" value={spreadVal} sx={{ width: 160 }}
                      helperText={`= ${(model.assumptions.prime_pct + (spreadVal ?? 0)).toFixed(2)}% now`}
                      inputProps={{ 'aria-label': `Spread for ${dbt.name}` }}
                      onChange={(v) => setDebt(dbt.account_id, (x) => { x.spread_pct = v; })} />
                  ) : (
                    <NumField label="Rate %" value={o?.rate_pct ?? (unconfirmed ? null : dbt.rate_pct)} sx={{ width: 200 }}
                      placeholder="enter rate" error={unconfirmed} helperText={unconfirmed ? rateHint : undefined}
                      inputProps={{ 'aria-label': `Rate for ${dbt.name}` }}
                      onChange={(v) => setDebt(dbt.account_id, (x) => { x.rate_pct = v; })} />
                  )}
                  <NumField label="Payment $/mo" value={o?.monthly_payment ?? dbt.monthly_payment} sx={{ width: 150 }}
                    helperText={o?.monthly_payment != null ? 'plan' : (dbt.payment_source ?? '').replace('transactions: ', 'from ')}
                    inputProps={{ 'aria-label': `Payment for ${dbt.name}` }}
                    onChange={(v) => setDebt(dbt.account_id, (x) => { x.monthly_payment = v; })} />
                  <TextField select size="small" label="Payment type" value={o?.payment_type ?? dbt.payment_type ?? 'fixed'} sx={{ minWidth: 190 }}
                    inputProps={{ 'aria-label': `Payment type for ${dbt.name}` }}
                    onChange={(e) => setDebt(dbt.account_id, (x) => { x.payment_type = e.target.value as 'fixed' | 'adjustable'; })}>
                    <MenuItem value="fixed">Fixed payment</MenuItem>
                    <MenuItem value="adjustable">Adjustable payment</MenuItem>
                  </TextField>
                  <FormControlLabel label="Include" control={
                    <Checkbox size="small" checked={!o?.exclude} inputProps={{ 'aria-label': `Include ${dbt.name}` }}
                      onChange={(e) => setDebt(dbt.account_id, (x) => { x.exclude = !e.target.checked; })} />} />
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Box data-testid="credit-lines">
        <Typography variant="subtitle2" gutterBottom>Credit lines (available credit — not assets, no effect until drawn)</Typography>
        {(model.credit_lines ?? []).map((c, i) => {
          const upd = (fn: (x: CreditLine) => void) => set((d) => fn(d.credit_lines![i]));
          return (
            <Stack key={i} direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 1 }} alignItems={{ md: 'flex-start' }} flexWrap="wrap" useFlexGap>
              <TextField size="small" label="Name" value={c.name} onChange={(e) => upd((x) => { x.name = e.target.value; })} />
              {!c.readvance_of_account_id && <NumField label="Limit" value={c.limit} placeholder="limit: enter amount" sx={{ width: 170 }}
                error={c.limit == null} helperText={c.limit == null ? 'limit: enter amount' : undefined}
                inputProps={{ 'aria-label': `Limit for ${c.name}` }}
                onChange={(v) => upd((x) => { x.limit = v; })} />}
              <NumField label="Spread to prime" value={c.spread_pct} placeholder="0.5" sx={{ width: 160 }}
                helperText={c.spread_pct == null ? 'assumed +0.5' : undefined}
                onChange={(v) => upd((x) => { x.spread_pct = v; })} />
              <TextField select size="small" label="Readvances on" value={c.readvance_of_account_id ?? ''} sx={{ minWidth: 300 }}
                inputProps={{ 'aria-label': `Readvances on for ${c.name}` }}
                onChange={(e) => upd((x) => { x.readvance_of_account_id = e.target.value || undefined; if (!e.target.value) x.limit_basis = null; })}>
                <MenuItem value="">— fixed limit —</MenuItem>
                {debts.filter((d) => d.account_id).map((d) => (
                  <MenuItem key={d.account_id} value={d.account_id}>{ov(d.account_id)?.label ?? d.label ?? d.name}</MenuItem>
                ))}
              </TextField>
              {c.readvance_of_account_id && (
                <>
                  <NumField label="Available $ on date" value={c.limit_basis?.amount ?? null} sx={{ width: 170 }}
                    inputProps={{ 'aria-label': `Available basis for ${c.name}` }}
                    onChange={(v) => upd((x) => { x.limit_basis = { amount: v ?? 0, as_of: x.limit_basis?.as_of ?? new Date().toISOString().slice(0, 10), source: x.limit_basis?.source ?? 'user' }; })} />
                  <TextField size="small" type="date" label="as of" value={c.limit_basis?.as_of ?? ''} InputLabelProps={{ shrink: true }}
                    onChange={(e) => upd((x) => { x.limit_basis = { amount: x.limit_basis?.amount ?? 0, as_of: e.target.value, source: x.limit_basis?.source ?? 'user' }; })} />
                  <TextField select size="small" label="Source" value={c.limit_basis?.source ?? 'user'} sx={{ minWidth: 110 }}
                    onChange={(e) => upd((x) => { x.limit_basis = { amount: x.limit_basis?.amount ?? 0, as_of: x.limit_basis?.as_of ?? '', source: e.target.value as 'user' | 'bank' }; })}>
                    <MenuItem value="user">user</MenuItem>
                    <MenuItem value="bank">bank</MenuItem>
                  </TextField>
                  <NumField label="Global limit (cap)" value={c.limit_cap ?? null} sx={{ width: 170 }}
                    onChange={(v) => upd((x) => { x.limit_cap = v; })} />
                </>
              )}
              <NumField label="Balance drawn" value={c.balance} sx={{ width: 160 }}
                onChange={(v) => upd((x) => { x.balance = v ?? 0; x.status = (v ?? 0) > 0 ? 'drawn' : 'unused'; })} />
              <IconButton size="small" aria-label={`Remove ${c.name}`} onClick={() => set((d) => { d.credit_lines = d.credit_lines!.filter((_, k) => k !== i); })}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        })}
        <Button size="small" startIcon={<AddIcon />}
          onClick={() => set((d) => { d.credit_lines = [...(d.credit_lines ?? []), { name: 'HELOC', limit: null, spread_pct: null, balance: 0, status: 'unused' }]; })}>
          Credit line
        </Button>
      </Box>
    </Stack>
  );
};
